import { getReq } from '../service/API'
import useSWR from 'swr'
import { BASE_URL } from '../constants'
import { Analytics, ContractGoal, enrollmentFunnelInOrder, EventType, Site } from '../Types'
import { SeriesForPlot } from '../pages/stats/utils'
import { useSearchParams } from 'react-router-dom'
import { useState } from 'react'
import { dataStore } from '../store/dataStore'
import { message } from 'antd'
const _ = require('lodash')

export type EnrollmentAnalyticData = {
  stateType: EventType
  _id: string
  siteId: string
  events: { createdAt: string; eventType: EventType }[]
}

export const useStatsSingleProject = (projectId: string) => {
  const [searchParams] = useSearchParams()
  const p = BASE_URL + `/dashboard/stats/${projectId}?${searchParams.toString()}`
  // startDate=${startDate}&endDate=${endDate}
  const [searching, setSearching] = useState<boolean>(false)
  const setSites = dataStore((state) => state.setSites)

  const fetchWithLog = async (p: string) => {
    try {
      setSearching(true)
      const isFirstLoad = !window.location.href?.includes('siteId')
      const url = isFirstLoad ? p + 'onlySanosSites=true' : p
      const res = await getReq(url)
      console.log(url.replaceAll(BASE_URL ?? '', ''), res)
      setSearching(false)
      return res
    } catch (err) {
      console.error('fetchWithLog >> err: ', err)
      const msg =
        typeof err === 'object' && err !== null && 'message' in err
          ? (err as { message: string }).message
          : 'Something went wrong - see console'
      message.error(msg)
      setSearching(false)
      throw err
    }
  }

  const { data, error } = useSWR(p, fetchWithLog, {
    suspense: true,
    revalidateIfStale: false,
    revalidateOnMount: false,
    revalidateOnFocus: false,
  })

  const projectAnalytics: Analytics | undefined = data.data.projectAnalytics
  const newestGoal: ContractGoal | undefined = projectAnalytics
    ? _.maxBy(projectAnalytics.goals, (goal) => new Date(goal.effectiveDate))
    : undefined

  const chartData = data?.data.enrollmentChartData

  const numSignUps: number = chartData?.numSignUps
  const numDbResponders: number = chartData?.numDbResponders

  const timelineChartData: { [event in EventType]: SeriesForPlot } = chartData?.timelineChartData
  const barChartData: {
    success: number[]
    failed: number[]
    unknown: number[]
  } = chartData?.barChartData

  const sitesSorted = sortSites(data?.data.sites)
  const currentSitesIds = dataStore
    .getState()
    .sites.map((site) => site._id)
    .join(';')
  const fetchedSitesIds = sitesSorted.map((site) => site._id).join(';')

  if (fetchedSitesIds !== currentSitesIds) {
    // new sites are fetched.
    setSites(sitesSorted)
  }

  const countsPerState = Object.entries(timelineChartData).reduce((acc, [eventName, series]) => {
    const lastPoint = series[series.length - 1]
    const count = lastPoint ? lastPoint[1] : 0
    acc[eventName] = count
    return acc
  }, {} as { [event in EventType]: number })

  return {
    timelineChartData,
    barChartData,
    numSignUps,
    numDbResponders,
    tableData: chartData?.tableData,
    projectAnalytics,
    sites: sitesSorted,
    countsPerState,
    facebookCampaignsInsights: data?.data.facebookCampaignsInsights as FacebookCampaignsInsights[],
    expectedRandomizations: chartData?.expectedRandomizations as ExpectedRandomizations,
    runInPeriod: chartData?.runInPeriod as number | undefined,
    newestGoal,
    isLoading: !error && !data,
    isError: error,
    searching,
  }
}

function sortSites(sites: Site[]) {
  const sanosSiteIdsSorted = sites
    .filter((site) => site.officialName.toLowerCase().includes('sanos'))
    .sort((a, b) => a.officialName.localeCompare(b.officialName))
  const nonSanosSiteIdsSorted = sites
    .filter((site) => !site.officialName.toLowerCase().includes('sanos'))
    .sort((a, b) => a.officialName.localeCompare(b.officialName))

  const sitesSorted = sanosSiteIdsSorted.concat(nonSanosSiteIdsSorted)
  return sitesSorted
}

/** E.g. groupedByPassedFunnelSteps.NEW_REFERRAL.SUCCESS. This includes all enrollments that have reached or passed this funnel step */
export type GroupedByPassedFunnelSteps = { [eventType in typeof enrollmentFunnelInOrder[number]]: GroupedByStatus }

type GroupedByStatus = { [status in PerformanceStatus]: EnrollmentAnalyticData[] }

export enum PerformanceStatus {
  Success = 'SUCCESS',
  Failed = 'FAILED',
  Unknown = 'UNKNOWN',
}

/** @example: {
    "impressions": "354435",
    "clicks": "4950",
    "spend": "35856.99",
    "ctr": "1.396589",
    "cpc": "7.243836",
    "cpm": "101.166617",
    "account_currency": "DKK",
    "campaign_id": "120213451629330706",
    "campaign_name": "Aribio I Gandrup",
    "unique_clicks": "3421",
} */
export type FacebookCampaignsInsights = {
  impressions: string
  clicks: string
  spend: string
  ctr: string
  cpc: string
  cpm: string
  account_currency: string
  campaign_id: string
  campaign_name: string
  unique_clicks: string
  /** unique impressions */
  reach: string
}

/** If we stop actively recruiting now, how many people do we expect to have enrollmend, based on those currently in the funnel? */
export type ExpectedRandomizations = {
  /** The number of enrollment we expect passes rando */
  passesRandomization: number
  /** The number of enrollments we expect passes screening, if recruitment is stopped now */
  passesScreening: number
  currentlyEnrolled: number
  /** When is the last person in the funnel finished? */
  expectedEnddate: string
}
